/** @jsxImportSource @emotion/react */

import { Plus } from 'lucide-react';

import { Board } from 'modules/core/domain/interfaces/supabase/overlay/entity';
import { Pages } from 'modules/navigation/domain/enums/pages';
import { useOrganizer } from 'modules/organizer/ui/hooks/use-organizer';
import Header from 'modules/template-editor/ui/components/catalog-card/header';
import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useIsAdmin } from 'shared/hooks/use-is-admin';
import { AppDispatch, history } from 'store/store';
import { useDispatchWithToast } from 'store/use-dispatch-with-toast';
import UDBreadcrumbs from 'ud-ui/components/breadcrumbs';
import EmptyButton from 'ud-ui/components/empty-button';
import useFabric from 'modules/overlay-canvas/hooks/use-fabric';
import BoardItem from 'modules/template-editor/ui/components/view-boards/boards-list/board-item';
import BoardsList from 'modules/template-editor/ui/components/view-boards/boards-list/boards-list';
import { CanvasWrapper, Container, LeftContainer, RightContainer } from 'modules/template-editor/ui/components/view-boards/styles';
import { useMainLayout } from 'modules/app-shell/ui/layout/context';
import { addView } from 'modules/template-editor/store/templates/actions';
import { selectActiveTemplate, selectBoardsByTemplateId } from 'modules/template-editor/store/templates/selectors';
import { setActiveBoard } from 'modules/template-editor/store/templates/templates';
import useCreateModal from '../hooks/modals/board/create';
import useBoards from '../hooks/use-boards';
import { MainBlock } from './styles';
import { theme } from 'styles/theme/theme';

function CatalogCard() {
  const dispatch = useDispatch<AppDispatch>();
  const dispatchWithToast = useDispatchWithToast();
  const { info } = useOrganizer();
  const id = useParams()?.id;
  const template = useSelector(selectActiveTemplate);
  const boards = useSelector(selectBoardsByTemplateId(template?.id));
  const { activeBoard } = useBoards();
  const { setHeaderContent } = useMainLayout();

  if (template === undefined) {
    history.push(Pages.TEMPLATE.CATALOG.INDEX);
  }

  useEffect(() => {
    if (!activeBoard || !template || !boards.find((board) => board.id === activeBoard.id)) {
      if (!boards || boards.length <= 0) {
        return;
      }
      dispatch(setActiveBoard({ id: boards[0].id }));
    }
  }, [activeBoard, boards, dispatch, template]);

  const breadcrumb = useMemo(
    () => (
      <UDBreadcrumbs
        className="my-10"
        breadcrumbs={[
          { label: 'Турниры', to: Pages.CHAMPIONSHIPS.INDEX },
          { label: 'Шаблоны', to: Pages.TEMPLATE.INDEX },
          { label: 'Готовые шаблоны', to: Pages.TEMPLATE.CATALOG.INDEX },
          {
            label: template?.name ?? 'Неизвестный шаблон',
            to: Pages.TEMPLATE.CATALOG.builders.card(id ? +id : -1),
          },
        ]}
      />
    ),
    [id, template?.name],
  );

  const { canvas, manager } = useFabric('static', {
    data: { ...template?.resolution },
    style: { borderRadius: 8, position: 'absolute' },
  });

  useEffect(() => {
    if (!manager) {
      return;
    }
    const resizeCanvas = () => {
      manager.setParentSize();
      manager.normalizedViewport();
    };

    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    return () => {
      window.removeEventListener('resize', resizeCanvas);
    };
  }, [manager]);

  useEffect(() => {
    if (!manager) {
      return;
    }
    if (!activeBoard) {
      return;
    }

    manager.loadLocal({
      scheme: { objects: activeBoard.objects, version: activeBoard.version },
      safeBackground: true,
    });
  }, [activeBoard, manager]);

  const onClick = (board: Board) => {
    dispatch(setActiveBoard({ id: board.id }));
  };

  useEffect(() => {
    if (!info) {
      return;
    }
    dispatchWithToast(addView({ templateId: id ? +id : -1, userId: info.id }), { error: 'Не удалось засчитать просмотр' });
  }, [dispatchWithToast, id, info]);

  const { modal: createModal, open: openCreateModal } = useCreateModal();

  const isAdmin = useIsAdmin();

  useEffect(() => {
    setHeaderContent(breadcrumb);
    return () => setHeaderContent(null);
  }, [breadcrumb, setHeaderContent]);

  return (
    <>
      <MainBlock>
        <Container>
          <LeftContainer>
            <BoardsList
              items={boards.map((board) => {
                return (
                  <BoardItem
                    key={board.id}
                    board={board}
                    isEditable={isAdmin}
                    onClick={() => onClick(board)}
                    isActive={board.id === activeBoard?.id}
                    isDefaultTemplate={true}
                  />
                );
              })}
              headerText={'Табло'}
            />
            {isAdmin && (
              <EmptyButton onClick={openCreateModal} color={theme.colors.Gray96} icon={<Plus size={16} />}>
                Добавить табло
              </EmptyButton>
            )}
          </LeftContainer>
          <RightContainer>
            <Header templateId={id ? +id : -1} />
            <CanvasWrapper>{canvas}</CanvasWrapper>
          </RightContainer>
        </Container>
      </MainBlock>
      {createModal}
    </>
  );
}

export default CatalogCard;
